import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  numeric,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants
// ---------------------------------------------------------------------------

export const PARTNER_TIERS = ["standard", "silver", "gold", "platinum"] as const;
export type PartnerTier = (typeof PARTNER_TIERS)[number];

export const PARTNER_STATUSES = ["applied", "approved", "suspended", "terminated"] as const;
export type PartnerStatus = (typeof PARTNER_STATUSES)[number];

export const CERTIFICATION_TYPES = ["product", "sales", "technical", "support"] as const;
export type CertificationType = (typeof CERTIFICATION_TYPES)[number];

export const CERTIFICATION_STATUSES = ["in_progress", "passed", "failed", "expired"] as const;
export type CertificationStatus = (typeof CERTIFICATION_STATUSES)[number];

export const REFERRAL_STATUSES = ["clicked", "signed_up", "subscribed", "churned"] as const;
export type ReferralStatus = (typeof REFERRAL_STATUSES)[number];

// ---------------------------------------------------------------------------
// 1. Partner Profiles
// ---------------------------------------------------------------------------

export const partnerProfiles = pgTable("partner_profiles", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(), // The partner's own tenant
  companyName: text("company_name").notNull(),
  // PII: contactEmail + contactName encrypted via KEK/DEK (same pattern as people.email)
  contactEmail: text("contact_email").notNull(), // Encrypted at rest
  contactEmailHash: text("contact_email_hash").notNull(), // HMAC for lookup
  contactName: text("contact_name").notNull(), // Encrypted at rest
  encryptionKeyId: text("encryption_key_id"),
  tier: text("tier").notNull().default("standard"), // PartnerTier
  commissionRate: numeric("commission_rate", { precision: 5, scale: 4 }).notNull().default("0.2000"), // 0.2000 = 20%
  status: text("status").notNull().default("applied"), // PartnerStatus
  website: text("website"),
  description: text("description"),
  appliedAt: timestamp("applied_at", tz).notNull().defaultNow(),
  approvedAt: timestamp("approved_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_partner_profile_tenant").on(table.tenantId),
  index("idx_partner_profile_status").on(table.status),
  index("idx_partner_profile_email").on(table.contactEmailHash),
]);

// ---------------------------------------------------------------------------
// 2. Partner Certifications
// ---------------------------------------------------------------------------

export const partnerCertifications = pgTable("partner_certifications", {
  id: uuid("id").primaryKey().defaultRandom(),
  partnerProfileId: uuid("partner_profile_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  certificationName: text("certification_name").notNull(),
  certificationType: text("certification_type").notNull(), // CertificationType
  status: text("status").notNull().default("in_progress"), // CertificationStatus
  score: integer("score"), // Nullable — only set on completion
  passedAt: timestamp("passed_at", tz),
  expiresAt: timestamp("expires_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_partner_cert_tenant").on(table.tenantId),
  index("idx_partner_cert_profile").on(table.tenantId, table.partnerProfileId),
  uniqueIndex("uq_partner_cert_profile_name").on(table.partnerProfileId, table.certificationName),
]);

// ---------------------------------------------------------------------------
// 3. Referral Tracking
// ---------------------------------------------------------------------------
// CROSS-TENANT: referrerTenantId (partner) and referredTenantId (new signup)
// are different tenants. This is a business relationship record.
//
// GDPR ERASURE STRATEGY (Council CQ3):
// - Referrer erased: anonymize referrerTenantId to 00000000-..., null PII, RETAIN row
// - Referred erased: anonymize referredTenantId, RETAIN row for referrer's financial records
// - Both anonymizations preserve the financial/commission record integrity

export const referralTracking = pgTable("referral_tracking", {
  id: uuid("id").primaryKey().defaultRandom(),
  referrerTenantId: uuid("referrer_tenant_id").notNull(), // Partner's tenant
  referredTenantId: uuid("referred_tenant_id"), // New signup's tenant (null until signup)
  referralCode: text("referral_code").notNull(), // Unique per partner, in UTM tracking
  status: text("status").notNull().default("clicked"), // ReferralStatus
  attributedRevenueCents: integer("attributed_revenue_cents").notNull().default(0),
  // BIL-D4: refunded revenue subtracted from attributed_revenue_cents when computing commission.
  // Atomic increments on refund.created (partial or full); MUST never exceed attributed_revenue_cents.
  refundedRevenueCents: integer("refunded_revenue_cents").notNull().default(0),
  currency: text("currency").notNull().default("EUR"),
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  clickedAt: timestamp("clicked_at", tz).notNull().defaultNow(),
  signedUpAt: timestamp("signed_up_at", tz),
  subscribedAt: timestamp("subscribed_at", tz),
  // GDPR: anonymizedAt set when either party requests erasure
  anonymizedAt: timestamp("anonymized_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_referral_referrer").on(table.referrerTenantId),
  index("idx_referral_referred").on(table.referredTenantId),
  index("idx_referral_code").on(table.referralCode),
  uniqueIndex("uq_referral_referred").on(table.referredTenantId), // A tenant can only be referred once
]);
